// Cache Service for PremPicks Application
// Provides localStorage-based caching with TTL and versioning
//
// CACHE VERSIONING:
// To force cache invalidation across all users on deployment:
// 1. Increment the CACHE_VERSION number below
// 2. All existing cache entries with older versions will be automatically cleared
// 3. Users will get fresh data on their next page load

class CacheService {
  constructor() {
    // Increment this version number when you need to force cache invalidation
    this.CACHE_VERSION = 2;
    
    this.defaultTTL = {
      userProfile: 24 * 60 * 60 * 1000, // 24 hours
      teamData: 24 * 60 * 60 * 1000, // 24 hours (user's favorite team)
      historicalStandings: 2 * 60 * 60 * 1000, // 2 hours
      leagueInfo: 2 * 60 * 60 * 1000, // 2 hours
      seasonConfig: 12 * 60 * 60 * 1000, // 12 hours
      userTitles: 6 * 60 * 60 * 1000, // 6 hours (titles change seasonally)
    };
  }

  /**
   * Generate cache key with prefix
   */
  getCacheKey(type, identifier = '') {
    return `prempicks_${type}_${identifier}`;
  }

  /**
   * Store data in cache with TTL
   */
  set(key, data, ttl = null) {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        ttl: ttl || this.defaultTTL[key.split('_')[1]] || (60 * 60 * 1000), // 1 hour default
        version: this.CACHE_VERSION
      };
      
      localStorage.setItem(key, JSON.stringify(cacheItem));
      return true;
    } catch (error) {
      console.warn('Failed to cache data:', error);
      return false;
    }
  }

  /**
   * Retrieve data from cache if not expired
   */
  get(key) {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheItem = JSON.parse(cached);
      const now = Date.now();
      
      // Check if version is outdated
      if (!cacheItem.version || cacheItem.version < this.CACHE_VERSION) {
        localStorage.removeItem(key);
        console.log(`Cache invalidated due to version mismatch: ${key}`);
        return null;
      }
      
      // Check if expired
      if (now > cacheItem.timestamp + cacheItem.ttl) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.warn('Failed to retrieve cached data:', error);
      return null;
    }
  }

  /**
   * Check if cached data exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Remove specific cache entry
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Failed to remove cached data:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries for this app
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('prempicks_')
      );
      keys.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.warn('Failed to clear cache:', error);
      return false;
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpired() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('prempicks_')
      );
      
      keys.forEach(key => {
        const cached = localStorage.getItem(key);
        if (cached) {
          const cacheItem = JSON.parse(cached);
          const now = Date.now();
          
          if (now > cacheItem.timestamp + cacheItem.ttl) {
            localStorage.removeItem(key);
          }
        }
      });
      return true;
    } catch (error) {
      console.warn('Failed to clear expired cache:', error);
      return false;
    }
  }

  // User Profile Caching
  setUserProfile(userId, profileData, ttl = null) {
    const key = this.getCacheKey('userProfile', userId);
    return this.set(key, profileData, ttl || this.defaultTTL.userProfile);
  }

  getUserProfile(userId) {
    const key = this.getCacheKey('userProfile', userId);
    return this.get(key);
  }

  removeUserProfile(userId) {
    const key = this.getCacheKey('userProfile', userId);
    return this.remove(key);
  }

  // Team Data Caching
  setTeamData(seasonYear, teamData, ttl = null) {
    const key = this.getCacheKey('teamData', seasonYear);
    return this.set(key, teamData, ttl || this.defaultTTL.teamData);
  }

  getTeamData(seasonYear) {
    const key = this.getCacheKey('teamData', seasonYear);
    return this.get(key);
  }

  // Historical Standings Caching
  setHistoricalStandings(leagueId, seasonYear, standingsData, ttl = null) {
    const key = this.getCacheKey('historicalStandings', `${leagueId}_${seasonYear}`);
    return this.set(key, standingsData, ttl || this.defaultTTL.historicalStandings);
  }

  getHistoricalStandings(leagueId, seasonYear) {
    const key = this.getCacheKey('historicalStandings', `${leagueId}_${seasonYear}`);
    return this.get(key);
  }

  // League Information Caching
  setLeagueInfo(leagueId, leagueData, ttl = null) {
    const key = this.getCacheKey('leagueInfo', leagueId);
    return this.set(key, leagueData, ttl || this.defaultTTL.leagueInfo);
  }

  getLeagueInfo(leagueId) {
    const key = this.getCacheKey('leagueInfo', leagueId);
    return this.get(key);
  }

  // Season Configuration Caching
  setSeasonConfig(seasonYear, configData, ttl = null) {
    const key = this.getCacheKey('seasonConfig', seasonYear);
    return this.set(key, configData, ttl || this.defaultTTL.seasonConfig);
  }

  getSeasonConfig(seasonYear) {
    const key = this.getCacheKey('seasonConfig', seasonYear);
    return this.get(key);
  }

  // League-specific data caching methods
  setLeagueMembers(leagueId, membersData, ttl = null) {
    const key = this.getCacheKey('leagueMembers', leagueId);
    return this.set(key, membersData, ttl || (2 * 60 * 60 * 1000)); // 2 hours
  }

  getLeagueMembers(leagueId) {
    const key = this.getCacheKey('leagueMembers', leagueId);
    return this.get(key);
  }

  setUserPredictions(userId, leagueId, season, predictionsData, ttl = null) {
    const key = this.getCacheKey('userPredictions', `${userId}_${leagueId}_${season}`);
    return this.set(key, predictionsData, ttl || (30 * 60 * 1000)); // 30 minutes
  }

  getUserPredictions(userId, leagueId, season) {
    const key = this.getCacheKey('userPredictions', `${userId}_${leagueId}_${season}`);
    return this.get(key);
  }

  setLeagueStandings(leagueId, season, standingsData, ttl = null) {
    const key = this.getCacheKey('leagueStandings', `${leagueId}_${season}`);
    // Disable caching for current season standings since they use real-time listeners
    return this.set(key, standingsData, ttl || (2 * 60 * 1000)); // 2 minutes (minimal cache)
  }

  getLeagueStandings(leagueId, season) {
    const key = this.getCacheKey('leagueStandings', `${leagueId}_${season}`);
    return this.get(key);
  }

  // Clear all data for a specific league
  clearLeagueData(leagueId) {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith('prempicks_') && key.includes(leagueId)
    );
    
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`🗑️ Cleared cached data for league: ${leagueId}`);
  }

  // Get cache size in human-readable format
  getCacheSizeFormatted() {
    const sizeBytes = this.getCacheStats().totalSize;
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1048576) return `${(sizeBytes / 1024).toFixed(1)} KB`;
    return `${(sizeBytes / 1048576).toFixed(1)} MB`;
  }

  // Utility method to get cache stats
  getCacheStats() {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith('prempicks_')
    );
    
    const stats = {
      totalEntries: keys.length,
      totalSize: keys.reduce((size, key) => {
        return size + (localStorage.getItem(key)?.length || 0);
      }, 0),
      types: {},
      leagues: new Set()
    };

    keys.forEach(key => {
      const parts = key.split('_');
      const type = parts[1];
      
      if (!stats.types[type]) {
        stats.types[type] = 0;
      }
      stats.types[type]++;

      // Extract league IDs from cache keys
      if (parts.length > 2 && type !== 'userProfile') {
        const identifier = parts.slice(2).join('_');
        const leagueId = identifier.split('_')[0];
        if (leagueId && leagueId.length > 5) { // Basic validation
          stats.leagues.add(leagueId);
        }
      }
    });

    stats.leagues = Array.from(stats.leagues);
    return stats;
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Export class for testing
export default CacheService;